import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { WINDOW_PARAM } from './navigation'

/**
 * Окна поверх экрана. Стопка живёт в адресе (`?w=`), а не в состоянии: даром
 * берутся ссылка на открытое окно, возврат после F5 и «назад» браузера,
 * который закрывает окно (перенос `useWindowStack` старого клиента).
 *
 * Ссылка на окно — `kind:параметры` из реестра (`refOf`): rid и ключ сущности
 * в ней, поэтому два окна одного вида для разных ресурсов — разные окна.
 */
export interface WindowStack {
  /** Ссылки снизу вверх; последняя — видимое окно. */
  refs: readonly string[]
  open: (ref: string, params?: Record<string, string>) => void
  closeTop: () => void
  /** Закрыть окно по ссылке, где бы оно в стопке ни было. */
  close: (ref: string) => void
  replace: (fromRef: string, ref: string) => void
}

export function useWindowStack(): WindowStack {
  const [current, setParams] = useSearchParams()
  const joined = current.getAll(WINDOW_PARAM).join('\n')
  const refs = useMemo(() => (joined === '' ? [] : joined.split('\n')), [joined])

  const replaceStack = useCallback(
    (next: readonly string[], params?: Record<string, string>) => {
      if (params === undefined && next.length === refs.length && next.every((value, at) => value === refs[at])) return
      const updated = new URLSearchParams(current)
      for (const [name, value] of Object.entries(params ?? {})) updated.set(name, value)
      updated.delete(WINDOW_PARAM)
      for (const value of next) updated.append(WINDOW_PARAM, value)
      // Закрытие не копит шаги истории, открытие — добавляет (им работает «назад»).
      setParams(updated, { replace: next.length < refs.length })
    },
    [current, refs, setParams],
  )

  return {
    refs,
    open: useCallback(
      (ref, params) => replaceStack([...refs.filter((item) => item !== ref), ref], params),
      [refs, replaceStack],
    ),
    closeTop: useCallback(() => replaceStack(refs.slice(0, -1)), [refs, replaceStack]),
    close: useCallback((ref) => replaceStack(refs.filter((item) => item !== ref)), [refs, replaceStack]),
    replace: useCallback(
      (fromRef, ref) => replaceStack([...refs.filter((item) => item !== fromRef && item !== ref), ref]),
      [refs, replaceStack],
    ),
  }
}
